'use client';

import { DeliveryMethodExtensionTimedOrdersDto } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { ComponentPropsWithoutRef, useContext, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useCartStore, CartContext } from '@/entities/cart';
import { VenueAvailabilityKind } from '@/entities/venue/lib/venue-availability.utils';
import { useVenueAvailabilityStore } from '@/entities/venue/model/venue-availability.store';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { Currency } from '@/shared/model/Currency';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import * as Tabs from '@/shared/ui/tabs';
import { CartTimedOrdersBoxTimeSlot } from './cart-timed-orders-box-time-slot';
import './cart-timed-orders-box.scss';
import {
    buildAvailableDates,
    resolveStaleTimedSelection,
    resolveTimedDateLabel,
    resolveTimedSlotSelection,
} from './timed-orders-calendar';
import { getTimedOrderSlots } from './timed-orders-slots.util';

type Props = ComponentPropsWithoutRef<typeof Card>;
export const CartTimedOrdersBox = (props: Props) => {
    const t = useTranslations();
    const locale = useLocale();
    const { formMethods } = useContext(CartContext);
    const [type, date, timeStart, latitude, longitude] = useWatch({
        control: formMethods.control,
        name: [
            'time.type',
            'time.timed.date',
            'time.timed.timeStart',
            'deliveryMethod.address.lat',
            'deliveryMethod.address.lon',
        ],
    }) as [
        'earliest' | 'timed',
        string | undefined,
        string | null | undefined,
        string | undefined,
        string | undefined,
    ];
    const selectedDeliveryMethod = useCartStore((s) => s.deliveryMethod);
    const openingHoursQuery = useQuery(QueryApi.Venue.queries.OpeningHours);
    const allOutages = useVenueAvailabilityStore((s) => s.allOutages);
    const availabilityKind = useVenueAvailabilityStore((s) => s.status.kind);
    const isClosedOrOutage =
        availabilityKind === VenueAvailabilityKind.Closed ||
        availabilityKind === VenueAvailabilityKind.Outage;

    const [calendarDay, setCalendarDay] = useState(() => dayjs().format('YYYY-MM-DD'));

    const availableDates = useMemo(() => buildAvailableDates(calendarDay), [calendarDay]);

    useEffect(() => {
        const refreshCalendarDay = () => {
            setCalendarDay(dayjs().format('YYYY-MM-DD'));
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                refreshCalendarDay();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refreshCalendarDay);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', refreshCalendarDay);
        };
    }, []);

    useEffect(() => {
        const next = resolveStaleTimedSelection({
            date,
            timeStart,
            availableDates,
            calendarDay,
        });
        if (next.date !== date) {
            formMethods.setValue('time.timed.date', next.date);
        }
        if (next.timeStart !== (timeStart ?? undefined)) {
            formMethods.setValue('time.timed.timeStart', next.timeStart);
        }
    }, [availableDates, calendarDay, date, timeStart, formMethods]);

    const extensionTimedOrders = selectedDeliveryMethod?.extensionTimedOrders as
        | DeliveryMethodExtensionTimedOrdersDto
        | undefined;
    const hasTimedOrders = !!extensionTimedOrders;

    // Suggest timed when venue becomes closed/outage — do not re-force on every type change
    // (user may switch back to earliest; submit stays disabled while closed — see CartSummaryBox).
    useEffect(() => {
        if (!isClosedOrOutage || !hasTimedOrders) {
            return;
        }
        formMethods.setValue('time.type', 'timed');
        if (!date && availableDates[0]) {
            formMethods.setValue('time.timed.date', availableDates[0]);
        }
    }, [isClosedOrOutage, hasTimedOrders, date, availableDates, formMethods]);

    const timeSlots = useMemo(() => {
        if (!openingHoursQuery.data || !hasTimedOrders || !date) {
            return [];
        }
        // calendarDay refreshes past-slot filter after tab focus / day rollover.
        void calendarDay;
        return getTimedOrderSlots({
            openingDays: openingHoursQuery.data,
            date,
            now: dayjs(),
            intervalMinutes: extensionTimedOrders?.timeIntervalMinutes ?? 60,
            minimumMinutesFromCurrentTime: extensionTimedOrders?.minimumMinutesFromCurrentTime ?? 0,
            outages: allOutages,
        });
    }, [
        openingHoursQuery.data,
        hasTimedOrders,
        date,
        calendarDay,
        extensionTimedOrders?.minimumMinutesFromCurrentTime,
        extensionTimedOrders?.timeIntervalMinutes,
        allOutages,
    ]);

    useEffect(() => {
        const nextTimeStart = resolveTimedSlotSelection({
            timeStart,
            slotStarts: timeSlots.map((slot) => slot.start.format('HH:mm')),
        });
        if (nextTimeStart !== (timeStart ?? undefined)) {
            formMethods.setValue('time.timed.timeStart', nextTimeStart);
        }
    }, [timeSlots, timeStart, formMethods]);

    const slotDateTimes = useMemo(() => timeSlots.map((slot) => slot.start.toISOString()), [timeSlots]);
    const lat = latitude ? parseFloat(latitude) : NaN;
    const lon = longitude ? parseFloat(longitude) : NaN;
    const ecoDiscountsQuery = useQuery({
        ...QueryApi.Order.queries.TimedOrderDiscounts({
            deliveryMethodId: selectedDeliveryMethod?.id ?? '',
            dateTimes: slotDateTimes,
            latitude: lat,
            longitude: lon,
        }),
        enabled:
            type === 'timed' &&
            !!selectedDeliveryMethod?.id &&
            Number.isFinite(lat) &&
            Number.isFinite(lon) &&
            slotDateTimes.length > 0,
    });

    if (!hasTimedOrders) {
        return null;
    }

    const handleDateChange = (direction: 'left' | 'right') => {
        if (!date) {
            return;
        }
        const currentIndex = availableDates.indexOf(date);
        if (direction === 'left' && currentIndex > 0) {
            formMethods.setValue('time.timed.date', availableDates[currentIndex - 1]);
            formMethods.setValue('time.timed.timeStart', undefined);
        } else if (direction === 'right' && currentIndex < availableDates.length - 1) {
            formMethods.setValue('time.timed.date', availableDates[currentIndex + 1]);
            formMethods.setValue('time.timed.timeStart', undefined);
        }
    };

    const localeDate = date
        ? new Date(date).toLocaleString(locale, {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
          })
        : '';

    const formattedDateTime =
        date && localeDate
            ? resolveTimedDateLabel({
                  date,
                  calendarDay,
                  localeDate,
                  // next-intl keys are a branded union; helper stays string-keyed for unit tests
                  t: (key, values) => t(key as 'CART_TIMED_ORDERS_BOX.TODAY_DATE', values),
              })
            : localeDate;

    return (
        <Card {...props} className={cn('cart-timed-orders-box', props.className)}>
            <h3 className="cart-timed-orders-box__title" data-testid="cart-timed-orders-title">
                {t('CART_TIMED_ORDERS_BOX.TITLE')}
            </h3>
            <Tabs.Root
                value={type}
                onValueChange={(value) => {
                    formMethods.setValue('time.type', value as 'earliest' | 'timed');
                    if (!date) {
                        formMethods.setValue('time.timed.date', availableDates[0]);
                    }
                }}
            >
                <Tabs.List>
                    <Tabs.Trigger value="earliest" data-testid="cart-timed-orders-tab-earliest">
                        {t('CART_TIMED_ORDERS_BOX.TAB_EARLIEST')}
                    </Tabs.Trigger>
                    <Tabs.Trigger value="timed" data-testid="cart-timed-orders-tab-timed">
                        {t('CART_TIMED_ORDERS_BOX.TAB_TIMED')}
                    </Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="timed" className="cart-timed-orders-box__tabs-content">
                    <div className="cart-timed-orders-box__date-selector">
                        <button
                            type="button"
                            className="cart-timed-orders-box__date-arrow-button"
                            data-testid="cart-timed-orders-date-arrow-left"
                            onClick={() => handleDateChange('left')}
                            disabled={!date || availableDates.indexOf(date) === 0}
                        >
                            <Icon
                                name="arrow-down"
                                width={20}
                                height={20}
                                className="cart-timed-orders-box__date-arrow-left"
                            />
                        </button>
                        <button
                            type="button"
                            className="cart-timed-orders-box__date-button"
                            data-testid="cart-timed-orders-date-button"
                        >
                            {formattedDateTime}
                        </button>
                        <button
                            type="button"
                            className="cart-timed-orders-box__date-arrow-button"
                            data-testid="cart-timed-orders-date-arrow-right"
                            onClick={() => handleDateChange('right')}
                            disabled={!date || availableDates.indexOf(date) === availableDates.length - 1}
                        >
                            <Icon
                                name="arrow-down"
                                width={20}
                                height={20}
                                className="cart-timed-orders-box__date-arrow-right"
                            />
                        </button>
                    </div>
                    <div
                        className={cn(
                            'cart-timed-orders-box__time-slots',
                            !timeSlots.length && 'cart-timed-orders-box__time-slots--empty',
                        )}
                    >
                        {timeSlots.length ? (
                            timeSlots.map((slot) => {
                                const slotIso = slot.start.toISOString();
                                return (
                                    <CartTimedOrdersBoxTimeSlot
                                        key={slot.start.format('HH:mm')}
                                        start={slot.start}
                                        end={slot.end}
                                        currency={Currency.CZK}
                                        isActive={timeStart === slot.start.format('HH:mm')}
                                        hasEcoDiscount={!!ecoDiscountsQuery.data?.[slotIso]?.hasDiscount}
                                        onClick={() =>
                                            formMethods.setValue(
                                                'time.timed.timeStart',
                                                timeStart === slot.start.format('HH:mm')
                                                    ? undefined
                                                    : slot.start.format('HH:mm'),
                                            )
                                        }
                                    />
                                );
                            })
                        ) : (
                            <span>{t('CART_TIMED_ORDERS_BOX.NO_TIMES_AVAILABLE')}</span>
                        )}
                    </div>
                    <div className="cart-timed-orders-box__eco-message">
                        <Icon name="leaf" width={24} height={24} />
                        <p data-testid="cart-timed-orders-eco-message">
                            {t('CART_TIMED_ORDERS_BOX.ECO_MESSAGE')}
                        </p>
                    </div>
                </Tabs.Content>
            </Tabs.Root>
        </Card>
    );
};
